import { useQuery } from '@tanstack/react-query';
import { Medal, Rocket, Trophy } from 'lucide-react';
import { getDomain, getRecommendations } from '../../lib/api';
import { Card } from '../ui/card';
import { SkeletonText } from '../ui/skeleton';
import { Pagination, usePagination } from '../ui/pagination';
import { usd } from '../../lib/graph-visuals';
import { topSaverPeople } from '../../lib/savings';

const MEDAL_COLORS = ['text-warning', 'text-muted', 'text-haze'];

/**
 * The domain's realized-savings leaderboard (gamification): everyone who has
 * banked proven savings here, ranked, with the top 3 highlighted. Shown only when
 * the domain's `showLeaderboard` setting is on. Paginated at 20 so a large
 * team stays scannable.
 */
export function DomainTopSavers({ domainId }: { domainId: string }) {
  const env = useQuery({
    queryKey: ['domain', domainId],
    queryFn: () => getDomain(domainId),
  });
  const recs = useQuery({
    queryKey: ['recommendations', domainId],
    queryFn: () => getRecommendations(domainId),
    refetchInterval: 60_000,
  });

  const savers = topSaverPeople(recs.data?.items ?? []);
  const pg = usePagination(savers, 20);

  // Respect the per-domain setting — render nothing when leaderboard is off.
  if (env.data && !env.data.showLeaderboard) return null;

  return (
    <Card className="p-5">
      <div className="mb-1 flex items-center gap-2">
        <Trophy aria-hidden className="h-4 w-4" />
        <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-haze">
          Top savers
        </h2>
      </div>
      <p className="mb-3 text-xs text-muted">
        Teammates ranked by proven monthly savings in this domain.
      </p>

      {!recs.data ? (
        <SkeletonText lines={3} lineClassName="h-4" />
      ) : savers.length === 0 ? (
        <p className="text-sm text-muted">
          No validated savings yet — be the first on the board.{' '}
          <Rocket aria-hidden className="inline h-4 w-4 align-text-bottom" />
        </p>
      ) : (
        <>
          <ol className="space-y-1.5">
            {pg.pageItems.map((s, i) => {
              const rank = pg.start + i;
              const top3 = rank < 3;
              return (
                <li
                  key={s.name}
                  className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm ${
                    top3 ? 'border border-hairline bg-surface-2' : ''
                  }`}
                >
                  <span aria-hidden className="w-6 flex-none text-center">
                    {top3 ? (
                      <Medal className={`inline h-4 w-4 ${MEDAL_COLORS[rank]}`} />
                    ) : (
                      <span className="text-muted tnum">{rank + 1}</span>
                    )}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-bone">{s.name}</span>
                  <span
                    className={`font-display font-bold tnum ${top3 ? 'text-iris' : 'text-haze'}`}
                  >
                    {usd(s.saving)}
                    <span className="text-[0.6rem] font-normal text-muted">/mo</span>
                  </span>
                </li>
              );
            })}
          </ol>
          <Pagination
            page={pg.page}
            pageCount={pg.pageCount}
            total={pg.total}
            start={pg.start}
            shown={pg.shown}
            onPrev={pg.prev}
            onNext={pg.next}
          />
        </>
      )}
    </Card>
  );
}
